/**
 * @fileoverview MaterialRequestService - Service for managing material requests and reservations
 */

import { OrdenProduccionRepository } from '../repositories/OrdenProduccionRepository';
import { ReservaInventarioRepository } from '../repositories/ReservaInventarioRepository';
import { SolicitudInventarioRepository } from '../repositories/SolicitudInventarioRepository';
import { InventoryRecordService } from './InventoryRecordService';
import { pb } from '../helpers/pocketbase_helper';
import { createModuleLogger } from '../utils/logger';
import { formatCommentWithTimestamp } from '../utils/dateHelper';

const logger = createModuleLogger('MaterialRequestService');

/**
 * Service for managing material requests and reservations for production orders
 * Handles the complete workflow from material addition to inventory management
 */
export const MaterialRequestService = {
  /**
   * Add material to a production order
   * @param {string} ordenId - Production order ID
   * @param {string} materialId - Material ID
   * @param {number} quantity - Required quantity
   * @param {boolean} inventarioLocal - Whether to use local inventory (true) or external sourcing (false)
   * @param {string} comentario - Optional comment from user
   * @returns {Promise<Object>} Result object with reservation/request details
   */
  async addMaterialToOrder(ordenId, productoInventarioId, quantity, inventarioLocal = true, comentario = null) {
    try {
      logger.info('Adding material to order', { ordenId, productoInventarioId, quantity, inventarioLocal });

      // Validate inputs
      if (!ordenId || !productoInventarioId || !quantity || quantity <= 0) {
        throw new Error('Invalid parameters: ordenId, productoInventarioId, and positive quantity are required');
      }

      // Get current user
      const usuarioId = pb.authStore.model?.id;
      if (!usuarioId) {
        throw new Error('Usuario no autenticado');
      }

      // Validate that producto_inventario exists and is active
      let inventoryRecord;
      try {
        inventoryRecord = await pb.collection('producto_inventario').getOne(productoInventarioId);
        if (inventoryRecord.activo === false) {
          throw new Error(`El producto de inventario "${productoInventarioId}" no está activo.`);
        }
      } catch (err) {
        throw new Error(`Producto de inventario "${productoInventarioId}" no encontrado. Por favor, seleccione un producto de inventario válido.`);
      }

      logger.info('Inventory record verified', { 
        inventoryId: inventoryRecord.id, 
        productoInventarioId,
        total: inventoryRecord.total 
      });

      // Check for existing reservation/request for this material in this order
      const existingAllocation = await this.findExistingAllocation(ordenId, inventoryRecord.id);

      let result = {
        reservado: 0,
        solicitado: 0,
        reservaId: null,
        solicitudId: null,
        inventoryRecord: inventoryRecord.id
      };

      if (inventarioLocal) {
        // Local inventory workflow
        result = await this.processLocalInventoryWorkflow(
          ordenId, 
          inventoryRecord, 
          quantity, 
          usuarioId, 
          existingAllocation,
          comentario
        );
      } else {
        // External sourcing workflow
        result = await this.processExternalSourcingWorkflow(
          ordenId, 
          inventoryRecord, 
          quantity, 
          usuarioId, 
          existingAllocation,
          comentario
        );
      }

      // Update order material arrays
      await this.updateOrderMaterialArrays(ordenId, result);

      logger.info('Material added to order successfully', { 
        ordenId, 
        productoInventarioId, 
        result 
      });

      return result;

    } catch (error) {
      logger.error('Error adding material to order', error, { ordenId, productoInventarioId });
      throw error;
    }
  },

  /**
   * Update material quantity in an order
   * @param {string} ordenId - Production order ID
   * @param {string} productoInventarioId - Producto Inventario ID
   * @param {number} newQuantity - New quantity
   * @returns {Promise<Object>} Result object
   */
  async updateMaterialQuantity(ordenId, productoInventarioId, newQuantity) {
    try {
      logger.info('Updating material quantity', { ordenId, productoInventarioId, newQuantity });

      if (newQuantity < 0) {
        throw new Error('Quantity cannot be negative');
      }

      // Find existing allocation
      const inventoryRecord = await pb.collection('producto_inventario').getOne(productoInventarioId);
      const existingAllocation = await this.findExistingAllocation(ordenId, inventoryRecord.id);

      if (!existingAllocation) {
        throw new Error('No existing allocation found for this material');
      }

      const currentQuantity = existingAllocation.total || 0;
      const quantityDifference = newQuantity - currentQuantity;

      if (quantityDifference === 0) {
        return { message: 'No change needed', reservado: currentQuantity, solicitado: 0 };
      }

      let result = { reservado: 0, solicitado: 0, reservaId: null, solicitudId: null };

      if (newQuantity === 0) {
        // Remove material completely
        result = await this.removeMaterialFromOrder(ordenId, productoInventarioId);
      } else if (quantityDifference > 0) {
        // Increase quantity
        result = await this.addMaterialToOrder(ordenId, productoInventarioId, quantityDifference, existingAllocation.inventario_local);
      } else {
        // Decrease quantity - return excess to inventory
        const excessQuantity = Math.abs(quantityDifference);
        result = await this.decreaseMaterialQuantity(ordenId, productoInventarioId, excessQuantity);
      }

      logger.info('Material quantity updated', { ordenId, productoInventarioId, newQuantity, result });
      return result;

    } catch (error) {
      logger.error('Error updating material quantity', error, { ordenId, productoInventarioId });
      throw error;
    }
  },

  /**
   * Update reservation quantity independently
   * @param {string} reservaId - Reservation ID
   * @param {number} newQuantity - New quantity
   * @returns {Promise<Object>} Result object
   */
  async updateReservationQuantity(reservaId, newQuantity) {
    try {
      logger.info('Updating reservation quantity', { reservaId, newQuantity });

      if (newQuantity < 0) {
        throw new Error('Quantity cannot be negative');
      }

      const reservaRepo = new ReservaInventarioRepository();
      const reserva = await reservaRepo.getById(reservaId);

      if (!reserva) {
        throw new Error('Reservation not found');
      }

      // Check if order is active
      let isOrdenActiva = false;
      try {
        const orden = await pb.collection('orden_produccion').getOne(reserva.orden_produccion);
        isOrdenActiva = !!orden.activa;
      } catch (err) {
        logger.warn('Could not check if order is active', { ordenId: reserva.orden_produccion, error: err });
      }

      const oldQuantity = reserva.total || 0;
      const quantityDifference = newQuantity - oldQuantity;

      if (quantityDifference === 0) {
        return { reservado: newQuantity, solicitado: 0, reservaId };
      }

      const productoInventarioId = reserva.producto_inventario;
      const inventario = await pb.collection("producto_inventario").getOne(productoInventarioId);

      // Get current user for return record
      const usuarioId = pb.authStore.model?.id;
      if (!usuarioId) {
        throw new Error('Usuario no autenticado');
      }

      // Calculate cantidadDevolucion if quantity is decreased
      const cantidadDevolucion = quantityDifference < 0 ? Math.abs(quantityDifference) : 0;

      // If quantity is decreased, create return record
      if (quantityDifference < 0) {
        const comentarioDevolucion = formatCommentWithTimestamp(`Devolución de ${cantidadDevolucion} unidades.`);

        // Create return record - copy inventario_local from original reservation
        await reservaRepo.create({
          orden_produccion: reserva.orden_produccion,
          producto_inventario: productoInventarioId,
          total: cantidadDevolucion,
          usuario: usuarioId,
          completado: true,
          activo: true,
          devolucion: true,
          inventario_local: reserva.inventario_local !== false, // Copy from original reservation (default to true)
          comentario: comentarioDevolucion
        });

        logger.info('Return record created', { 
          reservaId, 
          cantidadDevolucion, 
          ordenId: reserva.orden_produccion,
          inventario_local: reserva.inventario_local
        });
      }

      if (newQuantity === 0) {
        // Delete reservation (return record already created above if quantityDifference < 0)

        const reservasArray = inventario.reserva || [];
        const nuevoReservasArray = reservasArray.filter(id => id !== reservaId);
        const nuevoReservado = Math.max(0, (inventario.reservado || 0) - oldQuantity);
        
        // For non-active orders: when reservation is deleted, only decrease reservado
        // Do NOT increase total because it was never deducted (only reservado was increased)
        // For active orders: if reservation was completed, it was deducted from total, so restore it
        const updateData = {
          reserva: nuevoReservasArray,
          reservado: nuevoReservado
        };
        
        // Only restore total if order is active AND reservation was completed
        // Use cantidadDevolucion (which equals oldQuantity when newQuantity is 0) for consistency
        if (isOrdenActiva && reserva.completado) {
          updateData.total = Math.max(0, (inventario.total || 0) + oldQuantity);
        }
        // For non-active orders: do NOT change total when deleting reservation
        // When a request is accepted in a non-active order, total was never deducted
        // (material was added manually before accepting), so we should NOT add it back when deleting
        // Only decrease reservado, total remains unchanged

        await pb.collection("producto_inventario").update(productoInventarioId, updateData);

        await reservaRepo.delete(reservaId);

        return { reservado: 0, solicitado: 0 };
      } else if (quantityDifference > 0) {
        // Quantity increased - check if there's enough available inventory
        const disponible = inventario.total - inventario.reservado - inventario.solicitado;
        
        if (disponible < quantityDifference) {
          // Not enough inventory available
          // Reserve what's available, create request for rest
          const availableToReserve = Math.max(0, disponible);
          const cantidadToSolicitar = quantityDifference - availableToReserve;
          
          // Update reservation to new quantity (old + available)
          const newReservaQuantity = oldQuantity + availableToReserve;
          
          // Only update quantity, don't add comment
          // The comment doesn't make sense since the reservation didn't increase fully
          // Visualizing the reserved amount is sufficient without the message
          await reservaRepo.update(reservaId, {
            total: newReservaQuantity
          });
          
          // Update inventory - only add availableToReserve to reservado
          const nuevoReservado = Math.max(0, (inventario.reservado || 0) + availableToReserve);
          const updateData = {
            reservado: nuevoReservado
          };
          
          if (isOrdenActiva) {
            updateData.total = Math.max(0, (inventario.total || 0) - availableToReserve);
          }
          
          await pb.collection("producto_inventario").update(productoInventarioId, updateData);
          
          // Create request for the remaining quantity
          const solicitudResult = await this.createOrUpdateRequest(
            reserva.orden_produccion,
            productoInventarioId,
            cantidadToSolicitar,
            pb.authStore.model?.id,
            null, // No existing allocation for new request
            null // Don't copy comment
          );
          
          return {
            reservado: newReservaQuantity,
            solicitado: solicitudResult.solicitado,
            reservaId,
            solicitudId: solicitudResult.solicitudId
          };
        } else {
          // Enough inventory available - normal update
          const updateReservaData = {
            total: newQuantity
          };
          
          // Append increase message
          const aumentoMessage = formatCommentWithTimestamp(`Aumento de ${quantityDifference} unidades. Nueva cantidad: ${newQuantity}`);
          const existingComment = reserva.comentario || '';
          updateReservaData.comentario = existingComment
            ? `${existingComment}\n${aumentoMessage}`
            : aumentoMessage;
          
          await reservaRepo.update(reservaId, updateReservaData);
          
          // Update reservado in producto_inventario
          const nuevoReservado = Math.max(0, (inventario.reservado || 0) + quantityDifference);
          const updateData = {
            reservado: nuevoReservado
          };
          
          if (isOrdenActiva) {
            updateData.total = Math.max(0, (inventario.total || 0) - quantityDifference);
          }
          
          await pb.collection("producto_inventario").update(productoInventarioId, updateData);
          
          return { reservado: newQuantity, solicitado: 0, reservaId };
        }
      } else {
        // Update reservation - append comment for both increase and decrease
        const updateReservaData = {
          total: newQuantity
        };

        // If quantity decreased, append return message to existing comment
        if (quantityDifference < 0) {
          const disminucionMessage = formatCommentWithTimestamp(`Disminución de ${cantidadDevolucion} unidades. Nueva cantidad: ${newQuantity}`);
          const existingComment = reserva.comentario || '';
          updateReservaData.comentario = existingComment
            ? `${existingComment}\n${disminucionMessage}`
            : disminucionMessage;
        } else if (quantityDifference > 0) {
          // Append increase message to existing comment
          const aumentoMessage = formatCommentWithTimestamp(`Aumento de ${quantityDifference} unidades. Nueva cantidad: ${newQuantity}`);
          const existingComment = reserva.comentario || '';
          updateReservaData.comentario = existingComment
            ? `${existingComment}\n${aumentoMessage}`
            : aumentoMessage;
        } else {
          // No change, keep existing comment
          if (reserva.comentario) {
            updateReservaData.comentario = reserva.comentario;
          }
        }
        
        await reservaRepo.update(reservaId, updateReservaData);

        // Update reservado in producto_inventario
        const nuevoReservado = Math.max(0, (inventario.reservado || 0) + quantityDifference);
        const updateData = {
          reservado: nuevoReservado
        };
        
        // If order is active and quantity increased, deduct from total immediately
        if (isOrdenActiva && quantityDifference > 0) {
          updateData.total = Math.max(0, (inventario.total || 0) - quantityDifference);
        }
        // If order is active and quantity decreased, restore to total (only if reservation was completed)
        else if (isOrdenActiva && quantityDifference < 0 && reserva.completado) {
          updateData.total = Math.max(0, (inventario.total || 0) + cantidadDevolucion);
        }
        // If order is NOT active and quantity decreased: do NOT change total
        // When a request is accepted in a non-active order, the total was never deducted
        // (material was added manually before accepting), so we should NOT add it back when returning
        // Only decrease reservado, total remains unchanged
        // No need to add else if for !isOrdenActiva because total should not change

        await pb.collection("producto_inventario").update(productoInventarioId, updateData);

        return { reservado: newQuantity, solicitado: 0, reservaId };
      }
    } catch (error) {
      logger.error('Error updating reservation quantity', error, { reservaId, newQuantity });
      throw error;
    }
  },

  /**
   * Update request quantity independently
   * @param {string} solicitudId - Request ID
   * @param {number} newQuantity - New quantity
   * @returns {Promise<Object>} Result object
   */
  async updateRequestQuantity(solicitudId, newQuantity) {
    try {
      logger.info('Updating request quantity', { solicitudId, newQuantity });

      if (newQuantity < 0) {
        throw new Error('Quantity cannot be negative');
      }

      const solicitudRepo = new SolicitudInventarioRepository();
      const solicitud = await solicitudRepo.getById(solicitudId);

      if (!solicitud) {
        throw new Error('Request not found');
      }

      const oldQuantity = solicitud.total || 0;
      const quantityDifference = newQuantity - oldQuantity;

      if (quantityDifference === 0) {
        return { reservado: 0, solicitado: newQuantity, solicitudId };
      }

      const productoInventarioId = solicitud.producto_inventario;

      if (newQuantity === 0) {
        // Delete request - mark as devolucion and append message before deleting
        const existingComment = solicitud.comentario || '';
        const cancelacionMessage = formatCommentWithTimestamp('Solicitud cancelada completamente');
        const nuevoComentario = existingComment 
          ? `${existingComment}\n${cancelacionMessage}`
          : cancelacionMessage;

        // Update request to mark as devolucion and add message before deletion
        await solicitudRepo.update(solicitudId, {
          devolucion: true,
          comentario: nuevoComentario
        });

        await this.removeFromInventorySolicitudArray(
          productoInventarioId,
          solicitudId,
          oldQuantity
        );

        await solicitudRepo.delete(solicitudId);

        return { reservado: 0, solicitado: 0 };
      } else {
        // Update request - append comment for both increase and decrease
        const updateData = {
          total: newQuantity
        };

        // If quantity is decreased, append message to existing comment
        if (quantityDifference < 0) {
          const cantidadDisminuida = Math.abs(quantityDifference);
          const disminucionMessage = formatCommentWithTimestamp(`Disminución de ${cantidadDisminuida} unidades. Nueva cantidad: ${newQuantity}`);
          const existingComment = solicitud.comentario || '';
          updateData.comentario = existingComment
            ? `${existingComment}\n${disminucionMessage}`
            : disminucionMessage;
        } else if (quantityDifference > 0) {
          // Append increase message to existing comment
          const aumentoMessage = formatCommentWithTimestamp(`Aumento de ${quantityDifference} unidades. Nueva cantidad: ${newQuantity}`);
          const existingComment = solicitud.comentario || '';
          updateData.comentario = existingComment
            ? `${existingComment}\n${aumentoMessage}`
            : aumentoMessage;
        } else {
          // No change, keep existing comment
          if (solicitud.comentario) {
            updateData.comentario = solicitud.comentario;
          }
        }

        await solicitudRepo.update(solicitudId, updateData);

        // Update solicitado in producto_inventario
        await this.updateInventorySolicitadoQuantity(
          productoInventarioId,
          oldQuantity,
          newQuantity
        );

        return { reservado: 0, solicitado: newQuantity, solicitudId };
      }
    } catch (error) {
      logger.error('Error updating request quantity', error, { solicitudId, newQuantity });
      throw error;
    }
  },

  /**
   * Remove material from an order
   * @param {string} ordenId - Production order ID
   * @param {string} productoInventarioId - Producto Inventario ID
   * @returns {Promise<Object>} Result object
   */
  async removeMaterialFromOrder(ordenId, productoInventarioId) {
    try {
      logger.info('Removing material from order', { ordenId, productoInventarioId });

      const inventoryRecord = await pb.collection('producto_inventario').getOne(productoInventarioId);
      const existingAllocation = await this.findExistingAllocation(ordenId, inventoryRecord.id);

      if (!existingAllocation) {
        return { message: 'No allocation found to remove', reservado: 0, solicitado: 0 };
      }

      const quantityToReturn = existingAllocation.total || 0;

      // Delete reservation/request and update producto_inventario
      if (existingAllocation.type === 'reserva') {
        // Remove reserva ID from array and decrement reservado
        const inventario = await pb.collection("producto_inventario").getOne(inventoryRecord.id);
        const reservasArray = inventario.reserva || [];
        const nuevoReservasArray = reservasArray.filter(id => id !== existingAllocation.id);
        const nuevoReservado = Math.max(0, (inventario.reservado || 0) - quantityToReturn);
        
        await pb.collection("producto_inventario").update(inventoryRecord.id, {
          reserva: nuevoReservasArray,
          reservado: nuevoReservado
        });
        
        await new ReservaInventarioRepository().delete(existingAllocation.id);
      } else if (existingAllocation.type === 'solicitud') {
        // Remove solicitud ID from array and decrement solicitado
        await this.removeFromInventorySolicitudArray(
          inventoryRecord.id,
          existingAllocation.id,
          quantityToReturn
        );
        
        await new SolicitudInventarioRepository().delete(existingAllocation.id);
      }

      // Return quantity to inventory (only for local inventory)
      if (existingAllocation.inventario_local && quantityToReturn > 0 && existingAllocation.type === 'reserva') {
        // Note: reservado already decremented above, so we don't need to call returnQuantityToInventory
        // But we might need to update total if needed
      }

      // Update order material arrays
      await this.updateOrderMaterialArrays(ordenId, { reservado: 0, solicitado: 0 });

      logger.info('Material removed from order', { ordenId, productoInventarioId, quantityReturned: quantityToReturn });
      return { reservado: 0, solicitado: 0, quantityReturned: quantityToReturn };

    } catch (error) {
      logger.error('Error removing material from order', error, { ordenId, productoInventarioId });
      throw error;
    }
  },

  /**
   * Get materials for an order
   * @param {string} ordenId - Production order ID
   * @returns {Promise<Array>} Array of material allocations
   */
  async getOrderMaterials(ordenId) {
    try {
      logger.info('Getting order materials', { ordenId });

      const reservaRepo = new ReservaInventarioRepository();
      const solicitudRepo = new SolicitudInventarioRepository();

      // Get reservations
      const reservas = await reservaRepo.getByOrden(ordenId, 1, 1000);
      
      // Get requests
      const solicitudes = await solicitudRepo.getByOrden(ordenId, 1, 1000);

      // Combine and format results
      const materials = [
        ...reservas.items.map(r => ({ ...r, type: 'reserva' })),
        ...solicitudes.items.map(s => ({ ...s, type: 'solicitud' }))
      ];

      logger.info('Order materials retrieved', { ordenId, count: materials.length });
      return materials;

    } catch (error) {
      logger.error('Error getting order materials', error, { ordenId });
      throw error;
    }
  },

  /**
   * Check inventory availability for a producto_inventario
   * @param {string} productoInventarioId - Producto Inventario ID
   * @param {number} quantity - Required quantity
   * @returns {Promise<Object>} Availability information
   */
  async checkInventoryAvailability(productoInventarioId, quantity) {
    try {
      logger.info('Checking inventory availability', { productoInventarioId, quantity });

      const inventoryRecord = await pb.collection('producto_inventario').getOne(productoInventarioId);
      
      const total = inventoryRecord.total || 0;
      const reservado = inventoryRecord.reservado || 0;
      const solicitado = inventoryRecord.solicitado || 0;
      const disponible = total - reservado - solicitado;

      const isAvailable = disponible >= quantity;
      const shortfall = isAvailable ? 0 : quantity - disponible;

      const result = {
        materialId: productoInventarioId,
        total,
        reservado,
        solicitado,
        disponible,
        required: quantity,
        isAvailable,
        shortfall
      };

      logger.info('Inventory availability checked', result);
      return result;

    } catch (error) {
      logger.error('Error checking inventory availability', error, { productoInventarioId });
      throw error;
    }
  },

  // Private helper methods

  /**
   * Process local inventory workflow
   */
  async processLocalInventoryWorkflow(ordenId, inventoryRecord, quantity, usuarioId, existingAllocation, comentario = null) {
    const disponible = inventoryRecord.total - inventoryRecord.reservado - inventoryRecord.solicitado;
    
    let result = { reservado: 0, solicitado: 0, reservaId: null, solicitudId: null };

    if (disponible >= quantity) {
      // All available - create/update reservation
      result = await this.createOrUpdateReservation(
        ordenId, 
        inventoryRecord.id, 
        quantity, 
        usuarioId, 
        true, // inventario_local
        existingAllocation,
        comentario
      );
    } else if (disponible > 0) {
      // Partially available - create reservation + request
      const reservaQuantity = disponible;
      const solicitudQuantity = quantity - disponible;

      const reservaResult = await this.createOrUpdateReservation(
        ordenId, 
        inventoryRecord.id, 
        reservaQuantity, 
        usuarioId, 
        true, 
        existingAllocation,
        comentario
      );

      const solicitudResult = await this.createOrUpdateRequest(
        ordenId, 
        inventoryRecord.id, 
        solicitudQuantity, 
        usuarioId, 
        existingAllocation,
        comentario
      );

      result = {
        reservado: reservaResult.reservado,
        solicitado: solicitudResult.solicitado,
        reservaId: reservaResult.reservaId,
        solicitudId: solicitudResult.solicitudId
      };
    } else {
      // Not available - create request only
      result = await this.createOrUpdateRequest(
        ordenId, 
        inventoryRecord.id, 
        quantity, 
        usuarioId, 
        existingAllocation,
        comentario
      );
    }

    return result;
  },

  /**
   * Process external sourcing workflow
   */
  async processExternalSourcingWorkflow(ordenId, inventoryRecord, quantity, usuarioId, existingAllocation, comentario = null) {
    // For external sourcing, always create reservation with inventario_local=false
    return await this.createOrUpdateReservation(
      ordenId, 
      inventoryRecord.id, 
      quantity, 
      usuarioId, 
      false, // inventario_local
      existingAllocation,
      comentario
    );
  },

  /**
   * Create or update reservation
   * @param {string} comentario - Optional comment from user
   */
  async createOrUpdateReservation(ordenId, inventoryId, quantity, usuarioId, inventarioLocal, existingAllocation, comentario = null) {
    const reservaRepo = new ReservaInventarioRepository();

    // Check if order is active
    let isOrdenActiva = false;
    try {
      const orden = await pb.collection('orden_produccion').getOne(ordenId);
      isOrdenActiva = !!orden.activa;
    } catch (err) {
      logger.warn('Could not check if order is active', { ordenId, error: err });
    }

    // Always create a new reservation to maintain history
    // Don't update existing reservations, create new ones for each request
    const createData = {
      orden_produccion: ordenId,
      producto_inventario: inventoryId,
      total: quantity,
      usuario: usuarioId,
      completado: isOrdenActiva, // Mark as completed if order is active
      activo: true,
      inventario_local: inventarioLocal
    };
    if (comentario) {
      createData.comentario = comentario;
    }
    const reserva = await reservaRepo.create(createData);

    // Update inventory
    if (inventarioLocal) {
      if (isOrdenActiva) {
        // Order is active: deduct from total immediately, don't increase reservado
        const inventario = await pb.collection('producto_inventario').getOne(inventoryId);
        const currentReservas = inventario.reserva || [];
        const nuevoReservasArray = currentReservas.filter(id => id !== reserva.id); // Don't add to array
        
        await pb.collection('producto_inventario').update(inventoryId, {
          total: Math.max(0, (inventario.total || 0) - quantity),
          reservado: inventario.reservado || 0, // Keep reservado unchanged
          reserva: nuevoReservasArray // Don't add completed reservation to array
        });
      } else {
        // Order not active: normal flow
        await this.updateInventoryReserved(inventoryId, quantity, false);
        await this.addToInventoryReservaArray(inventoryId, reserva.id);
      }
    }

    return {
      reservado: quantity,
      reservaId: reserva.id,
      solicitado: 0,
      solicitudId: null
    };
  },

  /**
   * Create or update request
   * @param {string} comentario - Optional comment from user
   */
  async createOrUpdateRequest(ordenId, inventoryId, quantity, usuarioId, existingAllocation, comentario = null) {
    const solicitudRepo = new SolicitudInventarioRepository();

    if (existingAllocation && existingAllocation.type === 'solicitud') {
      // Update existing request
      const updateData = {
        total: (existingAllocation.total || 0) + quantity
      };
      // Update comment if provided (allow overwriting if user provides new comment)
      if (comentario) {
        updateData.comentario = comentario;
      }
      const updatedSolicitud = await solicitudRepo.update(existingAllocation.id, updateData);

      // Update inventory
      await this.updateInventorySolicitado(inventoryId, quantity);

      return {
        solicitado: quantity,
        solicitudId: updatedSolicitud.id,
        reservado: 0,
        reservaId: null
      };
    } else {
      // Create new request
      const createData = {
        orden_produccion: ordenId,
        producto_inventario: inventoryId,
        total: quantity,
        usuario: usuarioId,
        completado: false,
        activo: true
      };
      if (comentario) {
        createData.comentario = comentario;
      }
      const solicitud = await solicitudRepo.create(createData);

      // Update inventory
      await this.updateInventorySolicitado(inventoryId, quantity);

      // Add to inventory solicitud array
      await this.addToInventorySolicitudArray(inventoryId, solicitud.id);

      return {
        solicitado: quantity,
        solicitudId: solicitud.id,
        reservado: 0,
        reservaId: null
      };
    }
  },

  /**
   * Find existing allocation for material in order
   */
  async findExistingAllocation(ordenId, inventoryId) {
    try {
      // Check reservations
      const reservaRepo = new ReservaInventarioRepository();
      const reservas = await reservaRepo.getByOrden(ordenId, 1, 1000);
      const reserva = reservas.items.find(r => r.producto_inventario === inventoryId);
      
      if (reserva) {
        return { ...reserva, type: 'reserva' };
      }

      // Check requests
      const solicitudRepo = new SolicitudInventarioRepository();
      const solicitudes = await solicitudRepo.getByOrden(ordenId, 1, 1000);
      const solicitud = solicitudes.items.find(s => s.producto_inventario === inventoryId);
      
      if (solicitud) {
        return { ...solicitud, type: 'solicitud' };
      }

      return null;
    } catch (error) {
      logger.error('Error finding existing allocation', error, { ordenId, inventoryId });
      return null;
    }
  },

  /**
   * Get inventory record for material
   */
  async getInventoryRecordForMaterial(materialId) {
    try {
      // First, try to get the material and check if it has producto_inventario assigned
      let material;
      try {
        material = await pb.collection("material").getOne(materialId, {
          expand: 'producto_inventario'
        });
        
        if (material && material.producto_inventario) {
          // Get the producto_inventario record
          const productoInventario = await pb.collection("producto_inventario").getOne(material.producto_inventario, {
            expand: 'tinta'
          });
          
          if (productoInventario && productoInventario.activo !== false) {
            return productoInventario;
          }
        }
      } catch (materialErr) {
        // Material not found, try as tinta
        logger.debug('Material not found, trying as tinta', { materialId, error: materialErr.message });
      }

      // Try tinta field (for tinta-based producto_inventario)
      const tintaResult = await pb.collection("producto_inventario").getList(1, 1, {
        filter: `tinta = "${materialId}" && activo = true`,
        expand: 'tinta'
      });

      if (tintaResult.items.length > 0) {
        return tintaResult.items[0];
      }

      throw new Error(`No inventory record found for material ${materialId}. Please assign a producto_inventario to the material first.`);
    } catch (error) {
      logger.error('Error getting inventory record for material', error, { materialId });
      throw error;
    }
  },

  /**
   * Update inventory reserved quantity
   * @param {string} inventoryId - Inventory record ID
   * @param {number} quantity - Quantity to add to reserved
   * @param {boolean} isOrdenActiva - Whether the order is already active (if true, deduct from total immediately)
   */
  async updateInventoryReserved(inventoryId, quantity, isOrdenActiva = false) {
    const record = await pb.collection("producto_inventario").getOne(inventoryId);
    
    const updateData = {
      reservado: (record.reservado || 0) + quantity
    };
    
    // If order is active, deduct from total immediately since reservation won't be processed later
    if (isOrdenActiva) {
      updateData.total = Math.max(0, (record.total || 0) - quantity);
    }
    
    await pb.collection("producto_inventario").update(inventoryId, updateData);
  },

  /**
   * Update inventory requested quantity
   */
  async updateInventorySolicitado(inventoryId, quantity) {
    const record = await pb.collection("producto_inventario").getOne(inventoryId);
    
    await pb.collection("producto_inventario").update(inventoryId, {
      solicitado: (record.solicitado || 0) + quantity
    });
  },

  /**
   * Return quantity to inventory
   */
  async returnQuantityToInventory(inventoryId, quantity) {
    const record = await pb.collection("producto_inventario").getOne(inventoryId);
    
    await pb.collection("producto_inventario").update(inventoryId, {
      reservado: Math.max(0, (record.reservado || 0) - quantity)
    });
  },

  /**
   * Add reservation ID to inventory reserva array
   */
  async addToInventoryReservaArray(inventoryId, reservaId) {
    const record = await pb.collection("producto_inventario").getOne(inventoryId);
    
    const currentReservas = record.reserva || [];
    if (!currentReservas.includes(reservaId)) {
      await pb.collection("producto_inventario").update(inventoryId, {
        reserva: [...currentReservas, reservaId]
      });
    }
  },

  /**
   * Add request ID to inventory solicitud array
   */
  async addToInventorySolicitudArray(inventoryId, solicitudId) {
    const record = await pb.collection("producto_inventario").getOne(inventoryId);
    
    const currentSolicitudes = record.solicitud || [];
    if (!currentSolicitudes.includes(solicitudId)) {
      await pb.collection("producto_inventario").update(inventoryId, {
        solicitud: [...currentSolicitudes, solicitudId]
      });
    }
  },

  /**
   * Remove request ID from inventory solicitud array and decrement solicitado
   */
  async removeFromInventorySolicitudArray(inventoryId, solicitudId, cantidadSolicitada) {
    const record = await pb.collection("producto_inventario").getOne(inventoryId);
    
    // Remove solicitud ID from array
    const currentSolicitudes = record.solicitud || [];
    const nuevoSolicitudesArray = currentSolicitudes.filter(id => id !== solicitudId);
    
    // Decrement solicitado
    const nuevoSolicitado = Math.max(0, (record.solicitado || 0) - cantidadSolicitada);
    
    await pb.collection("producto_inventario").update(inventoryId, {
      solicitud: nuevoSolicitudesArray,
      solicitado: nuevoSolicitado
    });
  },

  /**
   * Update solicitado quantity when a request is modified
   */
  async updateInventorySolicitadoQuantity(inventoryId, oldQuantity, newQuantity) {
    const record = await pb.collection("producto_inventario").getOne(inventoryId);
    const diferencia = newQuantity - oldQuantity;
    
    if (diferencia !== 0) {
      const nuevoSolicitado = Math.max(0, (record.solicitado || 0) + diferencia);
      await pb.collection("producto_inventario").update(inventoryId, {
        solicitado: nuevoSolicitado
      });
    }
  },

  /**
   * Update order material arrays
   */
  async updateOrderMaterialArrays(ordenId, result) {
    try {
      const ordenRepo = new OrdenProduccionRepository();
      const order = await ordenRepo.getById(ordenId);
      
      const currentReservas = order.producto_inventario || [];
      const currentSolicitudes = order.producto_inventario || [];

      // Update arrays based on result
      if (result.reservaId && !currentReservas.includes(result.reservaId)) {
        currentReservas.push(result.reservaId);
      }
      if (result.solicitudId && !currentSolicitudes.includes(result.solicitudId)) {
        currentSolicitudes.push(result.solicitudId);
      }

      // Update order
      await ordenRepo.update(ordenId, {
        producto_inventario: [...new Set([...currentReservas, ...currentSolicitudes])]
      });

    } catch (error) {
      logger.error('Error updating order material arrays', error, { ordenId });
      // Don't throw - this is not critical
    }
  },

  /**
   * Decrease material quantity
   */
  async decreaseMaterialQuantity(ordenId, productoInventarioId, quantityToDecrease) {
    const inventoryRecord = await pb.collection('producto_inventario').getOne(productoInventarioId);
    const existingAllocation = await this.findExistingAllocation(ordenId, inventoryRecord.id);

    if (!existingAllocation) {
      return { reservado: 0, solicitado: 0 };
    }

    const oldQuantity = existingAllocation.total || 0;
    const newQuantity = Math.max(0, oldQuantity - quantityToDecrease);
    
    if (newQuantity === 0) {
      return await this.removeMaterialFromOrder(ordenId, productoInventarioId);
    } else {
      // Update existing allocation
      if (existingAllocation.type === 'reserva') {
        await new ReservaInventarioRepository().update(existingAllocation.id, {
          total: newQuantity
        });
        
        // Update reservado in producto_inventario
        const inventario = await pb.collection("producto_inventario").getOne(inventoryRecord.id);
        const nuevoReservado = Math.max(0, (inventario.reservado || 0) - quantityToDecrease);
        await pb.collection("producto_inventario").update(inventoryRecord.id, {
          reservado: nuevoReservado
        });
      } else {
        await new SolicitudInventarioRepository().update(existingAllocation.id, {
          total: newQuantity
        });
        
        // Update solicitado in producto_inventario
        await this.updateInventorySolicitadoQuantity(
          inventoryRecord.id,
          oldQuantity,
          newQuantity
        );
      }

      // Return excess to inventory (only for local inventory reservations)
      if (existingAllocation.inventario_local && existingAllocation.type === 'reserva') {
        // Note: reservado already updated above
      }

      return { 
        reservado: existingAllocation.type === 'reserva' ? newQuantity : 0, 
        solicitado: existingAllocation.type === 'solicitud' ? newQuantity : 0 
      };
    }
  }
};

export default MaterialRequestService;
